import { c as _c } from "react/compiler-runtime";
import { Alert, Button, Field, Heading, HStack, NativeSelect, Separator, Skeleton, Stack, Text } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import { selectionFromProduct, type ConversionOfferSelection } from './decision';
import { formatCents, productLabel, productPriceLabel, productRenewalLabel } from './presentation';
export type UpgradeableSubscriptionOption = {
  id: string;
  label: string;
  nextAssessmentAt?: string | null;
};

/** Caller-fetched estimate for this exact change (startups3 `scoSubscriptionChangeQuote`). */
export type ConversionUpgradeQuote = {
  dueNowInCents?: number | null;
  creditsAppliedInCents?: number | null;
  addedToNextInvoiceInCents?: number | null;
  nextBillingAt?: string | null;
  nextBillingAmountInCents?: number | null;
};
export type ConversionUpgradeReviewProps = {
  targetProduct: ScoProduct;
  subscriptions: UpgradeableSubscriptionOption[];
  selectedSubscriptionId?: string;
  onSelectSubscription?: (id: string) => void;
  onContinue?: (selection: ConversionOfferSelection & {
    subscriptionId: string;
  }) => void;
  onViewPlans?: () => void;
  loading?: boolean;
  error?: string | null;
  /**
   * When provided (or loading), the review shows the quoted amount due today and blocks the
   * confirm button until a quote is in hand: a plan change never charges an unstated amount.
   */
  quote?: ConversionUpgradeQuote | null;
  quoteLoading?: boolean;
  quoteError?: string | null;
  onRetryQuote?: () => void;
  /** Primary button copy. Defaults to "Review upgrade"; a caller that applies the change on click passes e.g. "Upgrade now". */
  confirmLabel?: string;
  /** Replaces "Move to {plan}" when the change is a billing term, not a different product. */
  title?: string;
  /** `plain` drops the panel chrome when the caller (a dialog) is already the surface. */
  variant?: 'card' | 'plain';
};

/** Reviews a potential change; the caller owns the subscription mutation and final charge disclosure. */
export const ConversionUpgradeReview = t0 => {
  const $ = _c(61);
  const {
    confirmLabel,
    error,
    loading,
    onContinue,
    onRetryQuote,
    onSelectSubscription,
    onViewPlans,
    quote,
    quoteError,
    quoteLoading,
    selectedSubscriptionId,
    subscriptions,
    targetProduct,
    title,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "card" : t1;
  let offer;
  let selected;
  let t2;
  if ($[0] !== selectedSubscriptionId || $[1] !== subscriptions || $[2] !== targetProduct) {
    selected = subscriptions.find(subscription => subscription.id === selectedSubscriptionId) ?? (subscriptions.length === 1 ? subscriptions[0] : undefined);
    let t3;
    if ($[6] !== targetProduct) {
      t3 = selectionFromProduct(targetProduct);
      $[6] = targetProduct;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    offer = t3;
    const renewalDate = selected?.nextAssessmentAt ? new Date(selected.nextAssessmentAt) : null;
    t2 = renewalDate && !Number.isNaN(renewalDate.getTime()) ? new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium"
    }).format(renewalDate) : null;
    $[0] = selectedSubscriptionId;
    $[1] = subscriptions;
    $[2] = targetProduct;
    $[3] = offer;
    $[4] = selected;
    $[5] = t2;
  } else {
    offer = $[3];
    selected = $[4];
    t2 = $[5];
  }
  const renewalLabel = t2;
  const quoted = quote !== undefined || Boolean(quoteLoading) || Boolean(quoteError);
  const quoteReady = !quoted || Boolean(quote) && !quoteLoading && !quoteError;
  let t3;
  if ($[8] !== quote) {
    const nextBillingDate = quote?.nextBillingAt ? new Date(quote.nextBillingAt) : null;
    t3 = nextBillingDate && !Number.isNaN(nextBillingDate.getTime()) ? new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium"
    }).format(nextBillingDate) : null;
    $[8] = quote;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const nextBillingLabel = t3;
  let t4;
  if ($[10] !== quote || $[11] !== targetProduct) {
    t4 = quote?.nextBillingAmountInCents != null ? formatCents(quote.nextBillingAmountInCents) : productRenewalLabel(targetProduct);
    $[10] = quote;
    $[11] = targetProduct;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const thenLabel = t4;
  let t5;
  if ($[13] !== variant) {
    t5 = variant === "card" ? {
      bg: "colorPalette.panel",
      borderColor: "border",
      borderRadius: "lg",
      borderWidth: "1px",
      maxW: "28rem",
      p: {
        base: 5,
        md: 6
      }
    } : {};
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      base: "lg",
      md: "xl"
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== targetProduct || $[17] !== title) {
    t7 = title?.trim() || `Move to ${productLabel(targetProduct)}`;
    $[16] = targetProduct;
    $[17] = title;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <Heading size={t6} textWrap="balance">{t7}</Heading>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== quoted || $[22] !== targetProduct) {
    t9 = quoted ? `${productPriceLabel(targetProduct)}. You pay the prorated difference today.` : `${productPriceLabel(targetProduct)} at renewal. A prorated charge may apply today.`;
    $[21] = quoted;
    $[22] = targetProduct;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <Text color="fg.muted" textStyle="sm">{t9}</Text>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t8) {
    t11 = <Stack gap={1.5}>{t8}{t10}</Stack>;
    $[26] = t10;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== onSelectSubscription || $[30] !== renewalLabel || $[31] !== selectedSubscriptionId || $[32] !== subscriptions) {
    t12 = subscriptions.length === 1 ? <Stack bg="colorPalette.container" borderRadius="md" gap={1} px={4} py={3}><Text color="fg.muted" textStyle="xs">Changing</Text><Text fontWeight="medium" textStyle="sm">{subscriptions[0].label}</Text>{renewalLabel ? <Text color="fg.muted" textStyle="xs">Current next renewal: {renewalLabel}</Text> : null}</Stack> : subscriptions.length > 1 ? <Field.Root gap={2}><Field.Label htmlFor="conversion-upgrade-subscription" textStyle="sm">Subscription to change</Field.Label><NativeSelect.Root><NativeSelect.Field id="conversion-upgrade-subscription" value={selectedSubscriptionId || ""} onChange={event => onSelectSubscription?.(event.target.value)}><option disabled={true} value="">Choose a subscription</option>{subscriptions.map(_temp)}</NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>{renewalLabel ? <Text color="fg.muted" textStyle="xs">Current next renewal: {renewalLabel}</Text> : null}</Field.Root> : <Text color="fg.muted" textStyle="sm">No eligible subscription is available to change.</Text>;
    $[29] = onSelectSubscription;
    $[30] = renewalLabel;
    $[31] = selectedSubscriptionId;
    $[32] = subscriptions;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== nextBillingLabel || $[35] !== onRetryQuote || $[36] !== quote || $[37] !== quoteError || $[38] !== quoteLoading || $[39] !== quoted || $[40] !== subscriptions.length || $[41] !== thenLabel) {
    t13 = quoted && subscriptions.length > 0 ? <Stack gap={2}><Separator />{quoteLoading ? <Stack aria-label="Calculating your upgrade price" gap={2}><Skeleton h={5} w="60%" /><Skeleton h={4} w="80%" /></Stack> : quoteError ? <Alert.Root status="warning"><Alert.Indicator /><Stack align="start" gap={2}><Text textStyle="sm">{quoteError}</Text>{onRetryQuote ? <Button size="xs" variant="outline" onClick={onRetryQuote}>Try again</Button> : null}</Stack></Alert.Root> : quote ? <Stack gap={1.5}><HStack align="baseline" justify="space-between"><Text fontWeight="medium" textStyle="sm">Due today</Text><Text fontVariantNumeric="tabular-nums" fontWeight="bold" textStyle="2xl">{formatCents(quote.dueNowInCents ?? 0)}</Text></HStack>{quote.creditsAppliedInCents ? <Text color="fg.muted" textStyle="xs">Includes a {formatCents(quote.creditsAppliedInCents)} credit for unused time on your current plan.</Text> : null}{quote.addedToNextInvoiceInCents ? <Text color="fg.muted" textStyle="xs">{formatCents(quote.addedToNextInvoiceInCents)} in prorated charges is added to your next invoice.</Text> : null}{thenLabel ? <Text color="fg.muted" textStyle="sm">{nextBillingLabel ? `Then ${thenLabel} from ${nextBillingLabel}` : `Then ${thenLabel}`}</Text> : null}</Stack> : null}</Stack> : null;
    $[34] = nextBillingLabel;
    $[35] = onRetryQuote;
    $[36] = quote;
    $[37] = quoteError;
    $[38] = quoteLoading;
    $[39] = quoted;
    $[40] = subscriptions.length;
    $[41] = thenLabel;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== error) {
    t14 = error ? <Alert.Root status="error"><Alert.Indicator /><Text>{error}</Text></Alert.Root> : null;
    $[43] = error;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== confirmLabel || $[46] !== loading || $[47] !== offer || $[48] !== onContinue || $[49] !== onViewPlans || $[50] !== quoteReady || $[51] !== selected || $[52] !== subscriptions.length) {
    t15 = subscriptions.length > 0 ? <Button disabled={!selected || !offer || loading || !quoteReady} variant="cta" onClick={() => {
      if (selected && offer) {
        onContinue?.({
          ...offer,
          subscriptionId: selected.id
        });
      }
    }}>{loading ? "Preparing\u2026" : confirmLabel || "Review upgrade"}</Button> : onViewPlans ? <Button variant="outline" onClick={onViewPlans}>See available plans</Button> : null;
    $[45] = confirmLabel;
    $[46] = loading;
    $[47] = offer;
    $[48] = onContinue;
    $[49] = onViewPlans;
    $[50] = quoteReady;
    $[51] = selected;
    $[52] = subscriptions.length;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== t11 || $[55] !== t12 || $[56] !== t13 || $[57] !== t14 || $[58] !== t15 || $[59] !== t5) {
    t16 = <Stack gap={5} w="full" {...t5}>{t11}{t12}{t13}{t14}{t15}</Stack>;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t5;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  return t16;
};
function _temp(subscription_0) {
  return <option key={subscription_0.id} value={subscription_0.id}>{subscription_0.label}</option>;
}